import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'sonner';
import { useAuth } from '@/layouts/RootLayout';
import { setUser } from '@/store/userSlice';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/kit';
import { FormDialog } from '@/components/inputs';
import { logActivity } from '@/shared/data';
import { EMAIL_RE, findAccount, joinName, lowerEmail, saveAccount, splitName, syncCustomerRecord } from '@/shared/userAccounts';

/**
 * Edit the first name, last name and email on a sign-in account.
 *
 * `target` is `{ id, email, firstName?, lastName?, name? }` (null keeps the dialog closed).
 * `self` edits the signed-in person: the session is updated after saving.
 */
export default function AccountDialog({ target, onOpenChange, onSaved, self = false, title, description }) {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '' });
  const [account, setAccount] = useState(null);
  const [saving, setSaving] = useState(false);
  const touched = useRef(false);

  useEffect(() => {
    if (!target) return;
    let live = true;
    touched.current = false;
    const fromName = splitName(target.name);
    setForm({
      firstName: target.firstName ?? fromName.firstName,
      lastName: target.lastName ?? fromName.lastName,
      email: target.email ?? '',
    });
    setAccount(self && target.id != null ? { Id: target.id, Email: target.email } : null);
    findAccount({ id: target.id, email: target.email }).then((found) => {
      if (!live || !found?.Id) return;
      setAccount(found);
      if (touched.current) return;
      setForm((f) => ({
        ...f,
        firstName: found.FirstName || f.firstName,
        lastName: found.LastName || f.lastName,
      }));
    });
    return () => {
      live = false;
    };
  }, [target, self]);

  const set = (key) => (e) => {
    touched.current = true;
    setForm((f) => ({ ...f, [key]: e.target.value }));
  };

  async function submit() {
    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();
    const email = lowerEmail(form.email);
    if (!firstName) {
      toast.error('Add a first name');
      return;
    }
    if (!EMAIL_RE.test(email)) {
      toast.error('Add a valid email address');
      return;
    }

    setSaving(true);
    try {
      const acc = account ?? (await findAccount({ id: target.id, email: target.email }));
      if (!acc?.Id) {
        toast.error("Couldn't find that sign-in account", {
          description: 'Only the owner can edit accounts. Refresh and try again.',
        });
        return;
      }

      const oldEmail = acc.Email ?? target.email;
      const result = await saveAccount({ ...acc, Email: oldEmail }, { firstName, lastName, email });
      if (!result.ok) {
        toast.error('Could not save the details', {
          description: self ? `${result.message}. Open a ticket and we'll change it for you.` : result.message,
        });
        return;
      }

      await syncCustomerRecord(oldEmail, {
        name: result.name,
        email: result.emailChanged ? result.email : null,
      });

      if (self && user) {
        dispatch(
          setUser({
            ...user,
            firstName,
            lastName,
            name: joinName(firstName, lastName),
            emailAddress: result.emailChanged ? result.email : user.emailAddress,
          }),
        );
      }

      const changes = [result.name, result.emailChanged ? `email → ${result.email}` : ''].filter(Boolean).join(' · ');
      logActivity(self ? 'Account' : 'Team', 'Details changed', oldEmail, changes);

      if (result.emailRejected) {
        toast.warning('Name saved. The email stayed the same.', { description: result.emailRejected });
      } else if (result.emailChanged) {
        toast.success('Details saved', {
          description: self ? 'Use the new email the next time you sign in.' : `They now sign in with ${result.email}.`,
        });
      } else {
        toast.success('Details saved');
      }

      onSaved?.(result);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <FormDialog
      open={!!target}
      onOpenChange={onOpenChange}
      title={title ?? (self ? 'My details' : `Edit ${target?.name || target?.email || 'account'}`)}
      description={description ?? 'The name shown across Ghost Cable and the email used to sign in.'}
      onSubmit={submit}
      saving={saving}
      submitLabel="Save details"
      busyLabel="Saving…"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="First name" htmlFor="acc-first">
          <Input id="acc-first" value={form.firstName} onChange={set('firstName')} placeholder="Rowan" autoComplete="given-name" />
        </Field>
        <Field label="Last name" htmlFor="acc-last">
          <Input id="acc-last" value={form.lastName} onChange={set('lastName')} placeholder="Blackwood" autoComplete="family-name" />
        </Field>
      </div>
      <Field
        label="Email"
        htmlFor="acc-email"
        hint={self ? 'Your sign-in email. Changing it changes how you sign in.' : 'Their sign-in email. Changing it changes how they sign in.'}
      >
        <Input
          id="acc-email"
          type="email"
          value={form.email}
          onChange={set('email')}
          placeholder="rowan@ghostcable.app"
          autoComplete="email"
        />
      </Field>
    </FormDialog>
  );
}
